import React, { useState, useContext, useEffect } from 'react'
import { MODULE_GROUP_THEME } from "@/lib/config/theme"
import { ColorPalette, colors } from '@aw/ui'

type DataType = { [key: string]: any }

export const ThemeContext = React.createContext<DataType>({})
type ThemeColors = ColorPalette

// NOT named `--primary-*` (that ramp is frozen). Only the `-l`/`-d` INPUTS are set here; styles.css
// resolves them per theme — setting resolved names inline would outrank `.dark` and pin dark to light.
// `bg` is a role, not colors[_color][500]: space-cadet's -500 IS #232B3D = --bg-2, so a filled primary painted its own background.
const PRIMARY_DARK_ROLES: Record<string, { bg: string; fg: string; hover: string }> = {
    purple: { bg: colors.purple[500], fg: '#FBFBFC', hover: colors.purple[600] },
    green: { bg: colors.green[500], fg: '#003D2D', hover: colors.green[300] },
    // Inverts to a light button in dark, so fg/hover flip with it.
    'space-cadet': { bg: colors['space-cadet'][50], fg: colors['space-cadet'][800], hover: '#FBFBFC' },
}

// Light label — today's `text-lavender-50`, palette-invariant.
const PRIMARY_LIGHT_FG = '#FDFDFE'

const useValue = () => {
    const [themeColor, setThemeColor] = useState<ThemeColors>('purple')

    function setTheme({ color }: { color: ThemeColors }) {
        const _color = color ?? 'purple'
        const root = document.documentElement;
        Object.entries(colors[_color]).forEach(([key, value]) => {
            root.style.setProperty(`--primary-${key}`, value);
        });
        const { bg, fg, hover } = PRIMARY_DARK_ROLES[_color] ?? PRIMARY_DARK_ROLES.purple
        root.style.setProperty('--btn-primary-fg-l', PRIMARY_LIGHT_FG)
        root.style.setProperty('--btn-primary-spinner-l', '#fff')
        // Bolted to the same source `--primary-500` / `--primary-600` read, so light can never desync.
        root.style.setProperty('--btn-primary-bg-l', colors[_color][500])
        root.style.setProperty('--btn-primary-hover-l', colors[_color][600])
        root.style.setProperty('--btn-primary-bg-d', bg)
        root.style.setProperty('--btn-primary-fg-d', fg)
        root.style.setProperty('--btn-primary-spinner-d', fg)
        root.style.setProperty('--btn-primary-hover-d', hover)
        setThemeColor(color || 'purple')
    }

    return {
        themeColor,
        setTheme
    }
}

export const ThemeProvider = ({ children }) => {
    return <ThemeContext.Provider value={useValue()}>
        {children}
    </ThemeContext.Provider>
}

// Hook to access and update theme
export const useTheme = () => {
    const context = useContext(ThemeContext)

    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider')
    }

    return {
        currentTheme: context.themeColor,
        setTheme: context.setTheme
    }
}
export const useColor = (moduleGroup?: keyof typeof MODULE_GROUP_THEME) => {
    const { setTheme } = useTheme()

    useEffect(() => {
        if (moduleGroup && MODULE_GROUP_THEME[moduleGroup]) {
            const themeConfig = MODULE_GROUP_THEME[moduleGroup]
            if (themeConfig.theme && themeConfig.theme in colors) {
                setTheme({ color: themeConfig.theme as ThemeColors })
            }
        }
    }, [moduleGroup, setTheme])

    const getModuleColors = (module: keyof typeof MODULE_GROUP_THEME) => {
        return MODULE_GROUP_THEME[module] || MODULE_GROUP_THEME.none
    }

    const setModuleColor = (module: keyof typeof MODULE_GROUP_THEME) => {
        const themeConfig = MODULE_GROUP_THEME[module]
        if (themeConfig?.theme && themeConfig.theme in colors) {
            setTheme({ color: themeConfig.theme as ThemeColors })
        }
    }

    return {
        getModuleColors,
        setModuleColor,
        moduleColors: moduleGroup ? MODULE_GROUP_THEME[moduleGroup] : null
    }
}